// ConciseRequestDetailsView.tsx - Clean, Figma-style request details view
// UPDATED: Added "Proceed with Endorsed Gates" partial approval flow
import React, { useState, useEffect } from 'react';
import { ParentRequestDetailPopup } from '../workFlow/ParentRequestDetailPopup';
import '../TasksView/TasksView.css';

interface Region {
  regionId: number;
  regionName: string;
  regionCode: string;
  description?: string;
  isActive: boolean;
}

interface Gate {
  gateId: number;
  gateNumber: string;
  locationId: number;
  locationName: string;
  isActive: boolean;
  description?: string;
  isExcluded?: boolean;
  isEndorsed?: boolean;
  isCancelledByUser?: boolean;
  excludedBy?: string;
  exclusionReason?: string;
}

interface Personnel {
  gatePassPersonnelId: number;
  personnelId: number;
  nationalIdNo: string;
  dateOfBirth: string;
  nationality: string;
  typeOfVisa?: string;
  visaExpiry?: string;
  bloodGroup?: string;
  photoFileName?: string;
  documentFileName?: string;
  companyId: number;
  companyName?: string;
  isExcluded?: boolean;
  excludedBy?: string;
  exclusionReason?: string;
}

interface RequestData {
  // Basic Information
  id: number;
  name: string;
  email: string;
  companyName: string;
  
  // Pass Information
  regionId?: number;
  requestType?: 'new' | 'supplementary';
  passType?: 'single_visit' | 'short_term' | 'long_term';
  contractNumber?: string;
  startDate?: string;
  endDate?: string;
  passIdentificationName?: string;
  parentRequestId?: number | null;
  
  // Request Details
  consignmentDetails: string;
  detailedDescription?: string;
  
  // Selections
  gates?: Gate[];
  personnel?: Personnel[];
  
  // File
  fileName?: string;
  fileSize?: number;
  fileType?: string;
  uploadedAt?: string;
  
  // Approval chain
  approverEmail?: string;
  reviewerEmail?: string;
  
  // Workflow information
  workflow?: {
    workflowId: number;
    currentState: string;
    stateChangedAt: string;
    stateChangedBy: string;
    pendingTasks: number;
    completedTasks: number;
    rejectedTasks: number;
    cancelledTasks: number;
    totalTasks: number;
    workflowProgress: number;
  } | null;
}

interface ConciseRequestDetailsViewProps {
  requestData: RequestData;
  regions?: Region[];
  onClose?: () => void;
  formatFileSize?: (bytes: number) => string;
  onDownloadFile?: (requestId: number, fileName: string) => void;
  onDownloadPersonnelFile?: (requestId: number, personnelId: number, fileType: 'photo' | 'document', fileName: string) => void;
  // NEW: Needed for "Proceed with Endorsed Gates" API call
  getAccessToken?: () => Promise<string>;
  onStatusUpdate?: (status: { type: 'success' | 'error' | null; message: string }) => void;
  onRefresh?: () => void;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://gatepass-api-1753632520.azurewebsites.net/api';

export const ConciseRequestDetailsView: React.FC<ConciseRequestDetailsViewProps> = ({
  requestData,
  regions = [],
  onClose,
  formatFileSize,
  onDownloadFile,
  onDownloadPersonnelFile,
  getAccessToken,
  onStatusUpdate,
  onRefresh
}) => {
  // NEW: State for the "Proceed with Endorsed Gates" flow
  const [showProceedConfirmation, setShowProceedConfirmation] = useState(false);
  const [proceedLoading, setProceedLoading] = useState(false);
  const [proceedResult, setProceedResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Parent request popup state
  const [showParentPopup, setShowParentPopup] = useState(false);

  // REMINDER: State for send reminder flow
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [reminderMessage, setReminderMessage] = useState('');
  const [reminderLoading, setReminderLoading] = useState(false);
  const [reminderResult, setReminderResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [lastReminderSentAt, setLastReminderSentAt] = useState<string | null>(null);
  const [canSendReminder, setCanSendReminder] = useState(true);
  const [nextReminderAvailableAt, setNextReminderAvailableAt] = useState<string | null>(null);
  const [reminderStatusLoaded, setReminderStatusLoaded] = useState(false);

  // Help guide visibility
  const [helpGuideVisible, setHelpGuideVisible] = useState(true);

  // ═══════════════════════════════════════════════════════════════════
  // Return/Rejection comments — fetched from audit logs for RETURNED/REJECTED requests
  // ═══════════════════════════════════════════════════════════════════
  const [returnComments, setReturnComments] = useState<{
    comments: string;
    actionType: string;
    performedBy: string;
    performedAt: string;
  } | null>(null);
  const [loadingReturnComments, setLoadingReturnComments] = useState(false);

  // Helper functions
  const formatDate = (dateString: string): string => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatPassType = (passType: string): string => {
    switch (passType) {
      case 'single_visit': return 'Single Visit';
      case 'short_term': return 'Short Term';
      case 'long_term': return 'Long Term';
      default: return passType;
    }
  };

  const formatRequestType = (requestType: string): string => {
    switch (requestType) {
      case 'new': return 'New';
      case 'supplementary': return 'Supplementary';
      default: return requestType;
    }
  };

  const getRegionDisplay = (regionId: number | null): string => {
    if (!regionId) return '—';
    
    if (!regions || regions.length === 0) {
      return `Region ID: ${regionId}`;
    }
    
    const region = regions.find(r => r.regionId === regionId);
    return region ? `${region.regionName}` : `Region ID: ${regionId}`;
  };

  const calculateDuration = (startDate: string, endDate: string): string => {
    if (!startDate || !endDate) return '—';
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return `${diffDays} day${diffDays !== 1 ? 's' : ''}`;
  };

  const isVisaExpired = (expiryDate: string): boolean => {
    if (!expiryDate) return false;
    return new Date(expiryDate) < new Date();
  };

  // Helper: stage-based progress from workflow state (matches backend logic)
  const calculateStageBasedProgress = (currentState: string): number => {
    // For terminal negative states, use the backend's workflowProgress
    // which reflects where the request actually was when it terminated
    if (['REJECTED', 'RETURNED', 'CANCELLED', 'EXPIRED'].includes(currentState)) {
      return requestData.workflow?.workflowProgress ?? 0;
    }

    const stateProgressMap: { [key: string]: number } = {
      'DRAFT':                       0,
      'SUBMITTED':                   10,
      'COMPANY_APPROVER_PENDING':    15,
      'COMPANY_REVIEWER_PENDING':    30,
      'GATE_REVIEWER_PENDING':       40,
      'GATE_APPROVER_PENDING':       55,
      'SECURITY_APPROVER_PENDING':   70,
      'PARTIALLY_APPROVED':          85,
      'FULLY_APPROVED':              100,
    };
    return stateProgressMap[currentState] ?? 0;
  };

  // Helper function to render progress bar
  const renderProgressBar = (_workflowProgress: number, currentState: string) => {
    const progress = calculateStageBasedProgress(currentState);
    const isCompleted = currentState === 'FULLY_APPROVED';
    const isRejected = currentState === 'REJECTED';
    
    let progressColor = '#3b82f6';
    if (isCompleted) progressColor = '#242424';
    if (isRejected) progressColor = '#ef4444';

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <div style={{
          width: '120px',
          height: '6px',
          backgroundColor: '#e2e8f0',
          borderRadius: '3px',
          overflow: 'hidden'
        }}>
          <div style={{
            width: `${progress}%`,
            height: '100%',
            backgroundColor: progressColor,
            borderRadius: '3px',
            transition: 'width 0.3s ease'
          }} />
        </div>
        <span style={{ fontSize: '12px', color: '#64748b', fontWeight: '500' }}>
          {progress}%
        </span>
      </div>
    );
  };

  // Helper function to get workflow state display
  const getWorkflowStateDisplay = (state: string): { label: string; color: string } => {
    const stateMap: { [key: string]: { label: string; color: string } } = {
      'DRAFT': { label: 'Draft', color: '#6c757d' },
      'SUBMITTED': { label: 'Submitted', color: '#007bff' },
      'COMPANY_APPROVER_PENDING': { label: 'Company Approval Pending', color: '#ffc107' },
      'COMPANY_REVIEWER_PENDING': { label: 'Company Review Pending', color: '#fd7e14' },
      'GATE_REVIEWER_PENDING': { label: 'Gate Review Pending', color: '#6f42c1' },
      'GATE_APPROVER_PENDING': { label: 'Gate Approval Pending', color: '#17a2b8' },
      'SECURITY_APPROVER_PENDING': { label: 'Security Approval Pending', color: '#0d6efd' },
      'PARTIALLY_APPROVED': { label: 'Partially Approved', color: '#6b7280' },
      'FULLY_APPROVED': { label: 'Accepted for Processing', color: '#374151' },
      'REJECTED': { label: 'Rejected', color: '#dc3545' },
      'RETURNED': { label: 'Returned', color: '#dc3545' },
      'CANCELLED': { label: 'Cancelled', color: '#6c757d' },
      'EXPIRED': { label: 'Expired', color: '#495057' }
    };
    return stateMap[state] || { label: state, color: '#6c757d' };
  };

  // ─── NEW: Proceed with Endorsed Gates logic ──────────────────────
  const gates = requestData.gates || [];
  const endorsedGates = gates.filter(g => !!g.isEndorsed && !g.isExcluded);
  const nonEndorsedNonExcludedGates = gates.filter(g => !g.isEndorsed && !g.isExcluded);

  // Show button when workflow is in gate approval/review pending AND at least one gate is endorsed
  const isGateApprovalStage = requestData.workflow?.currentState === 'GATE_APPROVER_PENDING'
    || requestData.workflow?.currentState === 'GATE_REVIEWER_PENDING';
  const canProceedWithEndorsed = isGateApprovalStage
    && endorsedGates.length > 0
    && nonEndorsedNonExcludedGates.length > 0  // Only show if there are gates still pending
    && !!getAccessToken;  // Only need getAccessToken to be available

  const handleProceedWithEndorsed = async () => {
    if (!getAccessToken) return;
    setProceedLoading(true);
    setProceedResult(null);
    try {
      const accessToken = await getAccessToken();
      const response = await fetch(
        `${API_BASE_URL}/gatepass/${requestData.id}/proceed-with-endorsed`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || result.message || 'Failed to proceed with endorsed gates');
      }

      setProceedResult({ type: 'success', message: result.message });
      setShowProceedConfirmation(false);
      onStatusUpdate?.({ type: 'success', message: result.message });
      // Refresh parent data to reflect new workflow state
      onRefresh?.();
    } catch (error: any) {
      setProceedResult({ type: 'error', message: error.message || 'Failed to proceed with endorsed gates' });
    } finally {
      setProceedLoading(false);
    }
  };
  // ────────────────────────────────────────────────────────────────

  // ─── REMINDER: Logic ─────────────────────────────────────────────
  const activeReminderStates = [
    'COMPANY_APPROVER_PENDING',
    'COMPANY_REVIEWER_PENDING',
    'GATE_REVIEWER_PENDING',
    'GATE_APPROVER_PENDING',
    'SECURITY_APPROVER_PENDING'
  ];
  const canShowReminder = !!requestData.workflow
    && activeReminderStates.includes(requestData.workflow.currentState)
    && !!getAccessToken;

  // Fetch last reminder status on mount
  useEffect(() => {
    if (!canShowReminder || !getAccessToken) return;

    const fetchReminderStatus = async () => {
      try {
        const accessToken = await getAccessToken();
        const response = await fetch(
          `${API_BASE_URL}/gatepass/${requestData.id}/last-reminder`,
          {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            setCanSendReminder(result.data.canSendReminder);
            setNextReminderAvailableAt(result.data.nextAvailableAt || null);
            if (result.data.lastReminder) {
              setLastReminderSentAt(result.data.lastReminder.sentAt);
            }
          }
        }
      } catch (err) {
        console.error('Failed to fetch reminder status:', err);
      } finally {
        setReminderStatusLoaded(true);
      }
    };

    fetchReminderStatus();
  }, [canShowReminder, requestData.id, getAccessToken]);

  // ═══════════════════════════════════════════════════════════════════
  // Fetch return/rejection comments from audit logs
  // ═══════════════════════════════════════════════════════════════════
  const isReturnedOrRejected = requestData.workflow?.currentState === 'RETURNED'
    || requestData.workflow?.currentState === 'REJECTED';

  useEffect(() => {
    if (!isReturnedOrRejected || !getAccessToken) {
      setReturnComments(null);
      return;
    }

    const fetchReturnComments = async () => {
      setLoadingReturnComments(true);
      try {
        const accessToken = await getAccessToken();
        const response = await fetch(`${API_BASE_URL}/gatepass/${requestData.id}/audit-logs`, {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data && result.data.length > 0) {
            // Find the most recent RETURNED or REJECTED action with comments
            const returnOrRejectLog = result.data.find((log: any) => {
              const actionType = (log.actionType || '').toUpperCase();
              return actionType.includes('RETURN') || actionType.includes('REJECT');
            });

            if (returnOrRejectLog) {
              let comments = '';
              let performedBy = returnOrRejectLog.performedByName || returnOrRejectLog.performedByEmail || 'Approver';

              // Extract comments from the log details JSON
              if (returnOrRejectLog.details) {
                try {
                  const details = typeof returnOrRejectLog.details === 'string'
                    ? JSON.parse(returnOrRejectLog.details)
                    : returnOrRejectLog.details;
                  comments = details.comments || details.reason || '';
                } catch {
                  // details may not be valid JSON
                }
              }

              // Fallback: check top-level comments field
              if (!comments && returnOrRejectLog.comments) {
                comments = returnOrRejectLog.comments;
              }

              if (comments) {
                setReturnComments({
                  comments,
                  actionType: returnOrRejectLog.actionType || requestData.workflow?.currentState || 'RETURNED',
                  performedBy,
                  performedAt: returnOrRejectLog.createdAt || ''
                });
              } else {
                setReturnComments(null);
              }
            } else {
              setReturnComments(null);
            }
          }
        }
      } catch (error) {
        console.warn('⚠️ Error fetching return comments:', error);
      } finally {
        setLoadingReturnComments(false);
      }
    };

    fetchReturnComments();
  }, [isReturnedOrRejected, requestData.id, getAccessToken, requestData.workflow?.currentState]);

  const handleSendReminder = async () => {
    if (!getAccessToken) return;
    setReminderLoading(true);
    setReminderResult(null);
    try {
      const accessToken = await getAccessToken();
      const response = await fetch(
        `${API_BASE_URL}/gatepass/${requestData.id}/send-reminder`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ message: reminderMessage })
        }
      );

      const result = await response.json();

      if (!response.ok) {
        // Handle 429 (rate limit) specifically
        if (response.status === 429) {
          setCanSendReminder(false);
          setLastReminderSentAt(result.lastSentAt || null);
          setNextReminderAvailableAt(result.nextAvailableAt || null);
        }
        throw new Error(result.error || result.message || 'Failed to send reminder');
      }

      setReminderResult({ type: 'success', message: result.message });
      setCanSendReminder(false);
      setLastReminderSentAt(result.data?.sentAt || new Date().toISOString());
      setNextReminderAvailableAt(result.data?.nextAvailableAt || null);
      setShowReminderModal(false);
      setReminderMessage('');
      onStatusUpdate?.({ type: 'success', message: `🔔 ${result.message}` });
    } catch (error: any) {
      setReminderResult({ type: 'error', message: error.message || 'Failed to send reminder' });
    } finally {
      setReminderLoading(false);
    }
  };

  const formatReminderTimeLeft = (): string => {
    if (!nextReminderAvailableAt) return '';
    const now = new Date();
    const next = new Date(nextReminderAvailableAt);
    const diffMs = next.getTime() - now.getTime();
    if (diffMs <= 0) return 'Available now';
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    if (hours > 0) return `Available in ${hours}h ${minutes}m`;
    return `Available in ${minutes}m`;
  };
  // ────────────────────────────────────────────────────────────────

  // Helper: infer which step a rejection/return/cancellation occurred at
  // Uses the backend workflowProgress to determine the stage
  const getTerminalStepId = (): number => {
    const progress = requestData.workflow?.workflowProgress ?? 100;
    // Map backend progress ranges to the step where the terminal event occurred
    if (progress <= 15) return 2;  // Rejected at/after submission → Company stage
    if (progress <= 35) return 2;  // Rejected during company approval
    if (progress <= 60) return 3;  // Rejected during gate approval
    if (progress <= 75) return 4;  // Rejected during security approval
    return 5;                       // Went through all stages
  };

  // Workflow steps for sidebar
  const getWorkflowStepState = (stepId: number): 'current' | 'completed' | 'rejected' | '' => {
    const currentState = requestData.workflow?.currentState || 'SUBMITTED';
    const isTerminal = ['REJECTED', 'RETURNED', 'CANCELLED', 'EXPIRED'].includes(currentState);

    if (isTerminal) {
      const terminalStep = getTerminalStepId();
      if (stepId < terminalStep) return 'completed';
      if (stepId === terminalStep) return 'rejected';
      return '';  // Steps beyond the terminal point stay empty
    }

    // Normal (non-terminal) flow
    const stateStepMap: { [key: string]: number } = {
      'DRAFT': 0, 'SUBMITTED': 1,
      'COMPANY_APPROVER_PENDING': 2, 'COMPANY_REVIEWER_PENDING': 2,
      'GATE_REVIEWER_PENDING': 3, 'GATE_APPROVER_PENDING': 3,
      'SECURITY_APPROVER_PENDING': 4,
      'PARTIALLY_APPROVED': 5, 'FULLY_APPROVED': 5,
    };
    const currentStepId = stateStepMap[currentState] ?? 1;
    if (stepId === currentStepId) return 'current';
    if (stepId < currentStepId) return 'completed';
    return '';
  };

  // Dynamic step 5 title based on terminal state
  const getStep5Title = (): string => {
    const currentState = requestData.workflow?.currentState || 'SUBMITTED';
    switch (currentState) {
      case 'REJECTED': return 'Rejected';
      case 'RETURNED': return 'Returned';
      case 'CANCELLED': return 'Cancelled';
      case 'EXPIRED': return 'Expired';
      case 'FULLY_APPROVED': return 'Completed';
      default: return 'Completed';
    }
  };

  const workflowSteps = [
    { id: 1, title: 'Submitted', desc: 'step 1' },
    { id: 2, title: 'Company Approvals', desc: 'step 2' },
    { id: 3, title: 'Gate Approvals', desc: 'step 3' },
    { id: 4, title: 'Security Approval', desc: 'step 4' },
    { id: 5, title: getStep5Title(), desc: 'step 5' },
  ];

  // Help content based on workflow state
  const getHelpContent = () => {
    const state = requestData.workflow?.currentState || 'SUBMITTED';
    if (state === 'FULLY_APPROVED') return {
      title: 'Request Approved',
      description: 'Your gate pass request has been fully approved and accepted for processing.',
      tips: ['The gate pass will be issued based on the approved details', 'You will receive notification when it is ready']
    };
    if (state === 'REJECTED') return {
      title: 'Request Rejected',
      description: 'Your request has been rejected. Check the workflow details for the reason.',
      tips: ['Review the rejection reason', 'You may submit a new request with corrections']
    };
    if (state === 'RETURNED') return {
      title: 'Returned for Revision',
      description: 'Your request has been returned. Please review the comments and resubmit.',
      tips: ['Check the comments for required changes', 'Edit the draft and resubmit']
    };
    if (state.includes('GATE')) return {
      title: 'Gate Review & Approval',
      description: 'Your request is currently being reviewed at the gate level. Each gate is reviewed independently.',
      tips: ['Some gates may be endorsed while others are pending', 'You can proceed with endorsed gates if needed', 'Use Send Reminder to nudge pending reviewers']
    };
    if (state.includes('COMPANY')) return {
      title: 'Company Review',
      description: 'Your request is being reviewed at the company level. This includes verification of contract details and personnel.',
      tips: ['The reviewer will verify your request details', 'You will be notified of any status changes', 'Use Send Reminder if the review is taking long']
    };
    if (state.includes('SECURITY')) return {
      title: 'Security Approval',
      description: 'All gate approvals are complete. Your request is now awaiting final security clearance.',
      tips: ['This is the final stage before the gate pass is issued', 'Security will review all consolidated access codes']
    };
    return {
      title: 'Request Status',
      description: 'Track the progress of your gate pass request through the approval workflow.',
      tips: ['Each stage must be completed before moving to the next', 'You will be notified of status changes', 'Use Send Reminder for pending stages']
    };
  };

  const helpInfo = getHelpContent();

  return (
    <div style={{ backgroundColor: '#ffffff', minHeight: '100vh', padding: '0px' }}>
      <div className="task-wizard-layout">

        {/* ── LEFT: Workflow Steps Sidebar (hidden for terminal states) ── */}
        {!isReturnedOrRejected && (
        <div className="task-wizard-sidebar">
          <div className="task-wizard-steps">
            {workflowSteps.map((step) => {
              const state = getWorkflowStepState(step.id);
              const isRejectedState = state === 'rejected';
              const currentState = requestData.workflow?.currentState || '';
              const isTerminal = ['REJECTED', 'RETURNED', 'CANCELLED', 'EXPIRED'].includes(currentState);
              return (
                <div
                  key={step.id}
                  className={`task-step-wrapper ${isRejectedState ? 'current' : state}`}
                  style={isRejectedState ? { } : undefined}
                >
                  <div
                    className="task-step-bar"
                    style={isRejectedState ? { backgroundColor: '#ef4444' } : undefined}
                  />
                  <div className="task-step-item">
                    <span
                      className="task-step-title"
                      style={isRejectedState ? { color: '#ef4444', fontWeight: 700 } : undefined}
                    >
                      {step.title}
                    </span>
                    <span
                      className="task-step-desc"
                      style={isRejectedState ? { color: '#ef4444' } : undefined}
                    >
                      {isRejectedState && isTerminal
                        ? (currentState === 'REJECTED' ? 'rejected here'
                          : currentState === 'RETURNED' ? 'returned here'
                          : currentState === 'CANCELLED' ? 'cancelled here'
                          : currentState === 'EXPIRED' ? 'expired here'
                          : step.desc)
                        : step.desc}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        )}

        {/* ── CENTER: Content ── */}
        <div className="task-wizard-content">

          {/* Compact Toolbar */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 0 12px 0',
            marginBottom: '16px',
            borderBottom: '1px solid #e2e8f0'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {onClose && (
                <button
                  onClick={onClose}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 14px',
                    backgroundColor: 'transparent', color: '#475569', border: '1px solid #e2e8f0',
                    borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '500',
                    transition: 'all 0.15s ease'
                  }}
                  onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#f3f4f6'; }}
                  onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                >
                  ← Back
                </button>
              )}
              <span style={{ color: '#d1d5db', fontSize: '14px' }}>/</span>
              <span style={{ fontSize: '14px', fontWeight: '600', color: '#242424' }}>
                Request #{requestData.id}
              </span>
            </div>
          </div>

          {/* Page Title */}
          <h1 style={{
            fontSize: '24px', fontWeight: 700, color: '#1a1a1a',
            margin: '0 0 24px 0', padding: 0, lineHeight: 1.3,
            fontFamily: "'Segoe UI', sans-serif", letterSpacing: '-0.01em',
          }}>
            Gate Pass Request Details
          </h1>

      {/* Content */}
      <div className="step-content">
        <div className="review-container">

          {/* ── Basic Information ─────────────────────────────────── */}
          <div className="selection-section">
            <div className="s2-section-header">
              <span className="s2-section-title">Request Information</span>
            </div>

            <div className="review-fields-grid">
              <div className="review-field">
                <span className="review-field-label">Name</span>
                <span className="review-field-value">{requestData.name}</span>
              </div>
              <div className="review-field">
                <span className="review-field-label">Email</span>
                <span className="review-field-value">{requestData.email}</span>
              </div>
              <div className="review-field">
                <span className="review-field-label">Company</span>
                <span className="review-field-value">{requestData.companyName}</span>
              </div>
              <div className="review-field">
                <span className="review-field-label">Region</span>
                <span className="review-field-value">{getRegionDisplay(requestData.regionId || null)}</span>
              </div>
              {requestData.requestType && (
                <div className="review-field">
                  <span className="review-field-label">Request Type</span>
                  <span className="review-field-value">{formatRequestType(requestData.requestType)}</span>
                </div>
              )}
              {requestData.passType && (
                <div className="review-field">
                  <span className="review-field-label">Pass Type</span>
                  <span className="review-field-value">{formatPassType(requestData.passType)}</span>
                </div>
              )}
              {requestData.startDate && (
                <div className="review-field">
                  <span className="review-field-label">Start Date</span>
                  <span className="review-field-value">{formatDate(requestData.startDate)}</span>
                </div>
              )}
              {requestData.endDate && (
                <div className="review-field">
                  <span className="review-field-label">End Date</span>
                  <span className="review-field-value">{formatDate(requestData.endDate)}</span>
                </div>
              )}
              {requestData.startDate && requestData.endDate && (
                <div className="review-field">
                  <span className="review-field-label">Duration</span>
                  <span className="review-field-value">{calculateDuration(requestData.startDate, requestData.endDate)}</span>
                </div>
              )}
              {requestData.contractNumber && (
                <div className="review-field">
                  <span className="review-field-label">Contract Number</span>
                  <span className="review-field-value">{requestData.contractNumber}</span>
                </div>
              )}
              {requestData.passIdentificationName && (
                <div className="review-field review-field--full">
                  <span className="review-field-label">Pass ID</span>
                  <span className="review-field-value">{requestData.passIdentificationName}</span>
                </div>
              )}
            </div>
          </div>

          {/* ── Parent Request Info (Supplementary only) ──────────── */}
          {(() => {
            if (requestData.requestType === 'supplementary') {
              console.log('🔍 Supplementary request detected. parentRequestId:', requestData.parentRequestId, '| Full requestData keys:', Object.keys(requestData));
            }
            return null;
          })()}
          {requestData.requestType === 'supplementary' && requestData.parentRequestId && (
            <div className="selection-section">
              <div className="s2-section-header">
                <span className="s2-section-title">Parent Request</span>
              </div>
              <div
                onClick={() => setShowParentPopup(true)}
                style={{
                  border: '1px solid #e2e8f0',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  backgroundColor: '#ffffff',
                  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease'
                }}
                onMouseOver={(e) => { e.currentTarget.style.borderColor = '#242424'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)'; }}
                onMouseOut={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)'; }}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px 16px',
                  backgroundColor: '#f8f9fa',
                  borderBottom: '1px solid #f1f5f9'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#242424' }}>
                      #{requestData.parentRequestId}
                    </span>
                    <span style={{
                      fontSize: '11px',
                      fontWeight: 600,
                      color: '#374151',
                      backgroundColor: '#f3f4f6',
                      padding: '2px 8px',
                      borderRadius: '4px'
                    }}>
                      Approved
                    </span>
                  </div>
                  <span style={{ fontSize: '12px', color: '#242424', fontWeight: 500 }}>
                    View Details →
                  </span>
                </div>
                <div style={{ padding: '12px 16px', fontSize: '14px', color: '#64748b' }}>
                  This is a supplementary request linked to parent request <span style={{ color: '#242424', fontWeight: 600 }}>#{requestData.parentRequestId}</span>.
                </div>
              </div>
            </div>
          )}

          {/* ── Gates ────────────────────────────────────── */}
          {requestData.gates && requestData.gates.length > 0 && (
            <div className="selection-section">
              <div className="s2-section-header">
                <span className="s2-section-title">Gates</span>
                <span className="s2-count-badge">{requestData.gates.length}</span>
              </div>
              <div className="selected-items-table">
                <table className="selection-table">
                  <thead>
                    <tr>
                      <th>Gate</th>
                      <th>Location</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {requestData.gates.map((gate) => {
                      const isExcluded = !!gate.isExcluded;
                      const isEndorsed = !!gate.isEndorsed && !isExcluded;
                      const isCancelledByUser = !!(gate.isCancelledByUser || gate.exclusionReason === 'CANCELLED_BY_USER');
                      return (
                        <tr key={gate.gateId} style={{ opacity: isExcluded ? 0.5 : 1 }}>
                          <td className="gate-number">
                            <strong style={{ textDecoration: isExcluded ? 'line-through' : 'none' }}>
                              Gate {gate.gateNumber}
                            </strong>
                          </td>
                          <td className="gate-location">{gate.locationName}</td>
                          <td>
                            {isCancelledByUser ? (
                              <span className="s2-warn-badge" style={{ backgroundColor: '#fefce8', color: '#854d0e', borderColor: '#fde68a' }}>Skipped</span>
                            ) : isExcluded ? (
                              <span className="s2-warn-badge">Excluded</span>
                            ) : isEndorsed ? (
                              <span style={{ fontSize: '12px', fontWeight: 500, color: '#374151', backgroundColor: '#f3f4f6', padding: '2px 8px', borderRadius: '20px', border: '1px solid #e2e8f0' }}>Endorsed</span>
                            ) : (
                              <span className={`status-text ${gate.isActive ? 'active' : 'inactive'}`}>
                                {gate.isActive ? 'Active' : 'Inactive'}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Proceed result message */}
              {proceedResult && (
                <div style={{
                  marginTop: '16px',
                  padding: '10px 14px',
                  backgroundColor: proceedResult.type === 'success' ? '#f3f4f6' : '#fef2f2',
                  border: `1px solid ${proceedResult.type === 'success' ? '#e2e8f0' : '#fecaca'}`,
                  borderRadius: '4px',
                  fontSize: '14px',
                  fontFamily: "'Segoe UI', sans-serif",
                  color: proceedResult.type === 'success' ? '#374151' : '#991b1b',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span>{proceedResult.type === 'success' ? '✅' : '❌'}</span>
                  {proceedResult.message}
                </div>
              )}
            </div>
          )}

          {/* ── Personnel ────────────────────────────────── */}
          {requestData.personnel && requestData.personnel.length > 0 && (
            <div className="selection-section">
              <div className="s2-section-header">
                <span className="s2-section-title">Personnel</span>
                <span className="s2-count-badge">{requestData.personnel.length}</span>
              </div>
              <div className="selected-items-table">
                <table className="selection-table">
                  <thead>
                    <tr>
                      <th>National ID</th>
                      <th>Nationality</th>
                      <th>DOB</th>
                      <th>Visa Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {requestData.personnel.map((person) => {
                      const expired = isVisaExpired(person.visaExpiry || '');
                      const isExcluded = !!person.isExcluded;
                      return (
                        <tr key={person.gatePassPersonnelId} className={expired && !isExcluded ? 'visa-issue-row' : ''} style={{ opacity: isExcluded ? 0.5 : 1 }}>
                          <td className="personnel-id">
                            <strong style={{ textDecoration: isExcluded ? 'line-through' : 'none', color: '#242424', fontWeight: 600, fontSize: '13px' }}>
                              {person.nationalIdNo}
                            </strong>
                            {isExcluded && (
                              <span className="s2-warn-badge" style={{ marginLeft: '8px' }}>Excluded</span>
                            )}
                          </td>
                          <td className="personnel-nationality">{person.nationality}</td>
                          <td className="personnel-date">
                            <span className="date-valid">{formatDate(person.dateOfBirth)}</span>
                          </td>
                          <td className="personnel-date">
                            {person.visaExpiry ? (
                              <span className={expired ? 'date-expired' : 'date-valid'}>
                                {formatDate(person.visaExpiry)}
                                {expired && !isExcluded && <span className="expiry-label">Expired</span>}
                              </span>
                            ) : <span className="date-missing">No Visa</span>}
                          </td>
                          <td>
                            {!isExcluded && (person.photoFileName || person.documentFileName) && (
                              <div style={{ display: 'flex', gap: '6px' }}>
                                {person.photoFileName && onDownloadPersonnelFile && (
                                  <button
                                    onClick={() => onDownloadPersonnelFile(requestData.id, person.personnelId, 'photo', person.photoFileName!)}
                                    style={{
                                      padding: '3px 8px', backgroundColor: '#242424', color: 'white',
                                      border: 'none', borderRadius: '4px', cursor: 'pointer',
                                      fontSize: '10px', fontWeight: '500'
                                    }}
                                  >
                                    📷
                                  </button>
                                )}
                                {person.documentFileName && onDownloadPersonnelFile && (
                                  <button
                                    onClick={() => onDownloadPersonnelFile(requestData.id, person.personnelId, 'document', person.documentFileName!)}
                                    style={{
                                      padding: '3px 8px', backgroundColor: '#7c3aed', color: 'white',
                                      border: 'none', borderRadius: '4px', cursor: 'pointer',
                                      fontSize: '10px', fontWeight: '500'
                                    }}
                                  >
                                    📄
                                  </button>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── Approval Chain ─────────────────────────────── */}
          {(requestData.approverEmail || requestData.reviewerEmail) && (
            <div className="selection-section">
              <div className="s2-section-header">
                <span className="s2-section-title">Approval Chain</span>
              </div>
              <div className="review-fields-grid">
                {requestData.approverEmail && (
                  <div className="review-field">
                    <span className="review-field-label">Approver</span>
                    <span className="review-field-value">{requestData.approverEmail}</span>
                  </div>
                )}
                {requestData.reviewerEmail && (
                  <div className="review-field">
                    <span className="review-field-label">Reviewer</span>
                    <span className="review-field-value">{requestData.reviewerEmail}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Additional Info ─────────────────────────────── */}
         

          {/* ── Workflow Status ─────────────────────────────── */}
          {requestData.workflow && (
            <div className="selection-section">
              <div className="s2-section-header">
                <span className="s2-section-title">Workflow Status</span>
              </div>
              <div className="review-fields-grid">
                <div className="review-field">
                  <span className="review-field-label">Current Status</span>
                  <div style={{ marginTop: '2px' }}>
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '4px 10px',
                      borderRadius: '4px',
                      fontSize: '13px',
                      fontWeight: '600',
                      backgroundColor: getWorkflowStateDisplay(requestData.workflow.currentState).color + '15',
                      color: getWorkflowStateDisplay(requestData.workflow.currentState).color,
                      border: `1px solid ${getWorkflowStateDisplay(requestData.workflow.currentState).color}30`
                    }}>
                      {getWorkflowStateDisplay(requestData.workflow.currentState).label}
                    </span>
                  </div>
                </div>
                <div className="review-field">
                  <span className="review-field-label">Overall Progress</span>
                  <div style={{ marginTop: '4px' }}>
                    {renderProgressBar(requestData.workflow.workflowProgress, requestData.workflow.currentState)}
                  </div>
                </div>
                <div className="review-field">
                  <span className="review-field-label">Last Updated</span>
                  <span className="review-field-value">{formatDate(requestData.workflow.stateChangedAt)}</span>
                </div>
              </div>

              {/* Reminder result message */}
              {reminderResult && !showReminderModal && (
                <div style={{
                  marginTop: '16px',
                  padding: '10px 14px',
                  backgroundColor: reminderResult.type === 'success' ? '#f3f4f6' : '#fef2f2',
                  border: `1px solid ${reminderResult.type === 'success' ? '#e2e8f0' : '#fecaca'}`,
                  borderRadius: '4px',
                  fontSize: '14px',
                  fontFamily: "'Segoe UI', sans-serif",
                  color: reminderResult.type === 'success' ? '#374151' : '#991b1b',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span>{reminderResult.type === 'success' ? '✅' : '❌'}</span>
                  {reminderResult.message}
                </div>
              )}
            </div>
          )}

          {/* ── Return / Rejection Comments (bottom) ──────────────────── */}
          {isReturnedOrRejected && (
            <div className="selection-section">
              <div className="s2-section-header">
                <span className="s2-section-title">
                  {requestData.workflow?.currentState === 'REJECTED' ? 'Rejection' : 'Return'} Comments
                </span>
              </div>

              {/* Loading state */}
              {loadingReturnComments && (
                <div style={{
                  padding: '16px 0',
                  fontSize: '13px',
                  color: '#6b7280',
                  fontStyle: 'italic'
                }}>
                  Loading comments...
                </div>
              )}

              {/* Comments card */}
              {returnComments && (
                <div style={{
                  padding: '14px 16px',
                  backgroundColor: '#f9fafb',
                  border: '1px solid #e5e7eb',
                  borderLeft: '3px solid #9ca3af',
                  borderRadius: '6px',
                }}>
                  {returnComments.performedAt && (
                    <div style={{
                      marginBottom: '8px',
                      fontSize: '12px',
                      color: '#6b7280',
                      textAlign: 'right'
                    }}>
                      {new Date(returnComments.performedAt).toLocaleDateString('en-GB', {
                        day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                      })}
                    </div>
                  )}
                  <div style={{
                    fontSize: '14px',
                    color: '#1f2937',
                    lineHeight: 1.6,
                    whiteSpace: 'pre-wrap'
                  }}>
                    {returnComments.comments}
                  </div>
                </div>
              )}

              {/* No comments fallback */}
              {!loadingReturnComments && !returnComments && (
                <div style={{
                  padding: '16px 0',
                  fontSize: '13px',
                  color: '#6b7280',
                  fontStyle: 'italic'
                }}>
                  No comments were provided.
                </div>
              )}
            </div>
          )}

        </div>{/* end review-container */}
      </div>{/* end step-content */}

        {/* ─── Actions Bar (bottom) ─────────────────────────────────── */}
        {(canProceedWithEndorsed || (canShowReminder && reminderStatusLoaded)) && (
          <div style={{
            borderTop: '1px solid #e2e8f0',
            padding: '20px 0 0 0',
            marginTop: '8px',
            fontFamily: "'Segoe UI', sans-serif",
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '16px',
              flexWrap: 'wrap'
            }}>
              {/* Left: contextual info */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
                {canProceedWithEndorsed && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{
                      width: '8px', height: '8px', borderRadius: '50%',
                      backgroundColor: '#374151', flexShrink: 0
                    }} />
                    <span style={{ fontSize: '14px', color: '#242424', fontFamily: "'Segoe UI', sans-serif" }}>
                      {endorsedGates.length} gate{endorsedGates.length !== 1 ? 's' : ''} endorsed
                      <span style={{ color: '#6b7280' }}> · </span>
                      {nonEndorsedNonExcludedGates.length} pending
                    </span>
                  </div>
                )}
                {canShowReminder && reminderStatusLoaded && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{
                      width: '8px', height: '8px', borderRadius: '50%',
                      backgroundColor: canSendReminder ? '#f59e0b' : '#d1d5db', flexShrink: 0
                    }} />
                    <span style={{ fontSize: '14px', color: '#6b7280', fontFamily: "'Segoe UI', sans-serif" }}>
                      {lastReminderSentAt
                        ? `Last reminder: ${formatDate(lastReminderSentAt)}`
                        : 'No reminders sent'
                      }
                      {!canSendReminder && nextReminderAvailableAt && (
                        <span style={{ marginLeft: '4px' }}>· {formatReminderTimeLeft()}</span>
                      )}
                    </span>
                  </div>
                )}
              </div>

              {/* Right: action buttons */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
                {canShowReminder && reminderStatusLoaded && (
                  <button
                    onClick={() => {
                      setReminderResult(null);
                      setReminderMessage('');
                      setShowReminderModal(true);
                    }}
                    disabled={!canSendReminder}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: canSendReminder ? '#ffffff' : '#f8fafc',
                      color: canSendReminder ? '#242424' : '#9ca3af',
                      border: canSendReminder ? '1px solid #d1d5db' : '1px solid #e2e8f0',
                      borderRadius: '4px',
                      cursor: canSendReminder ? 'pointer' : 'not-allowed',
                      fontSize: '14px', fontWeight: '600',
                      fontFamily: "'Segoe UI', sans-serif",
                      display: 'flex', alignItems: 'center', gap: '6px',
                      transition: 'all 0.15s ease',
                      whiteSpace: 'nowrap' as const
                    }}
                    onMouseOver={(e) => { if (canSendReminder) { e.currentTarget.style.backgroundColor = '#f9fafb'; e.currentTarget.style.borderColor = '#242424'; } }}
                    onMouseOut={(e) => { if (canSendReminder) { e.currentTarget.style.backgroundColor = '#ffffff'; e.currentTarget.style.borderColor = '#d1d5db'; } }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                    {canSendReminder ? 'Send Reminder' : 'Reminder Sent'}
                  </button>
                )}
                {canProceedWithEndorsed && (
                  <button
                    onClick={() => setShowProceedConfirmation(true)}
                    disabled={proceedLoading}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: proceedLoading ? '#f9a68e' : '#F3694A',
                      color: '#ffffff',
                      border: '1px solid transparent',
                      borderRadius: '4px',
                      cursor: proceedLoading ? 'not-allowed' : 'pointer',
                      fontSize: '14px', fontWeight: '600',
                      fontFamily: "'Segoe UI', sans-serif",
                      display: 'flex', alignItems: 'center', gap: '6px',
                      transition: 'all 0.15s ease',
                      opacity: proceedLoading ? 0.8 : 1,
                      whiteSpace: 'nowrap' as const
                    }}
                    onMouseOver={(e) => { if (!proceedLoading) e.currentTarget.style.backgroundColor = '#e05a3d'; }}
                    onMouseOut={(e) => { if (!proceedLoading) e.currentTarget.style.backgroundColor = '#F3694A'; }}
                  >
                    {proceedLoading ? (
                      <>
                        <span style={{
                          display: 'inline-block', width: '14px', height: '14px',
                          border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff',
                          borderRadius: '50%', animation: 'spin 0.8s linear infinite'
                        }} />
                        Processing...
                      </>
                    ) : (
                      <>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
                        Proceed with Endorsed Gates
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
        {/* ─── END: Actions Bar ──────────────────────────────────────── */}

        </div>{/* end task-wizard-content */}

        {/* ── RIGHT: Help Guide Panel ── */}
        <div className={`task-wizard-help ${helpGuideVisible ? '' : 'collapsed'}`}>
          <button
            type="button"
            className="task-help-toggle"
            onClick={() => setHelpGuideVisible(!helpGuideVisible)}
            aria-label={helpGuideVisible ? 'Hide Guide' : 'Show Guide'}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <circle cx="12" cy="12" r="10" />
              {helpGuideVisible ? (
                <path d="M15 9l-6 3 6 3" />
              ) : (
                <path d="M9 9l6 3-6 3" />
              )}
            </svg>
            <span>{helpGuideVisible ? 'Hide Guide' : 'Show Guide'}</span>
          </button>

          {helpGuideVisible && (
            <div className="task-help-content">
              <div className="task-help-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              </div>
              <p className="task-help-title">{helpInfo.title}</p>
              <p className="task-help-text">{helpInfo.description}</p>
              <p className="task-help-subtitle">Tips</p>
              <ul className="task-help-list">
                {helpInfo.tips.map((tip, i) => (
                  <li key={i}>{tip}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

      </div>{/* end task-wizard-layout */}

      {/* ─── NEW: Confirmation Modal ─────────────────────────────── */}
      {showProceedConfirmation && (
        <div
          onClick={(e) => {
            if (e.target === e.currentTarget && !proceedLoading) {
              setShowProceedConfirmation(false);
            }
          }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px'
          }}
        >
          <div style={{
            background: '#ffffff',
            borderRadius: '4px',
            width: '100%',
            maxWidth: '520px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
            overflow: 'hidden'
          }}>
            {/* Modal Header */}
            <div style={{
              padding: '24px 24px 0 24px',
              fontFamily: "'Segoe UI', sans-serif"
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '8px',
                backgroundColor: '#FEF0EC',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '16px'
              }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#F3694A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
              </div>
              <h3 style={{
                margin: '0 0 8px 0',
                fontSize: '15px',
                fontWeight: '600',
                color: '#242424',
                fontFamily: "'Segoe UI', sans-serif"
              }}>
                Proceed with Endorsed Gates?
              </h3>
              <p style={{
                margin: '0',
                fontSize: '14px',
                color: '#6b7280',
                lineHeight: '1.5',
                fontFamily: "'Segoe UI', sans-serif"
              }}>
                The approval will proceed to <strong style={{ color: '#242424' }}>Security Approval</strong> with 
                the <strong style={{ color: '#242424' }}>{endorsedGates.length}</strong> endorsed gate{endorsedGates.length !== 1 ? 's' : ''} only.
              </p>
            </div>

            {/* Modal Body - Gate Summary */}
            <div style={{ padding: '20px 24px' }}>
              {/* Endorsed gates */}
              <div style={{
                backgroundColor: '#f3f4f6',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                padding: '12px 16px',
                marginBottom: '12px'
              }}>
                <div style={{ fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  ✅ Will proceed ({endorsedGates.length})
                </div>
                <div style={{ fontSize: '13px', color: '#374151' }}>
                  {endorsedGates.map(g => g.gateNumber).join(', ')}
                </div>
              </div>

              {/* Gates that will be cancelled */}
              {nonEndorsedNonExcludedGates.length > 0 && (
                <div style={{
                  backgroundColor: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '8px',
                  padding: '12px 16px',
                  marginBottom: '12px'
                }}>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: '#991b1b', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    ❌ Will be cancelled ({nonEndorsedNonExcludedGates.length})
                  </div>
                  <div style={{ fontSize: '13px', color: '#b91c1c' }}>
                    {nonEndorsedNonExcludedGates.map(g => g.gateNumber).join(', ')}
                  </div>
                </div>
              )}

              {/* Warning */}
              <div style={{
                backgroundColor: '#fffbeb',
                border: '1px solid #fde68a',
                borderRadius: '8px',
                padding: '12px 16px',
                fontSize: '13px',
                color: '#92400e',
                lineHeight: '1.5'
              }}>
                <strong>⚠️ This action cannot be undone.</strong> The workflow will not wait for the remaining gate{nonEndorsedNonExcludedGates.length !== 1 ? 's' : ''} to be approved.
              </div>
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '16px 24px 24px 24px',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              fontFamily: "'Segoe UI', sans-serif"
            }}>
              <button
                onClick={() => setShowProceedConfirmation(false)}
                disabled={proceedLoading}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#ffffff',
                  color: '#242424',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  cursor: proceedLoading ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  fontFamily: "'Segoe UI', sans-serif"
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleProceedWithEndorsed}
                disabled={proceedLoading}
                style={{
                  padding: '8px 20px',
                  backgroundColor: proceedLoading ? '#f9a68e' : '#F3694A',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: proceedLoading ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  fontFamily: "'Segoe UI', sans-serif",
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.15s ease'
                }}
              >
                {proceedLoading ? (
                  <>
                    <span style={{
                      display: 'inline-block',
                      width: '14px',
                      height: '14px',
                      border: '2px solid rgba(255,255,255,0.3)',
                      borderTopColor: '#fff',
                      borderRadius: '50%',
                      animation: 'spin 0.8s linear infinite'
                    }} />
                    Processing...
                  </>
                ) : (
                  <>Yes, Proceed with Endorsed Gates</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ─── END: Confirmation Modal ─────────────────────────────── */}

      {/* ─── REMINDER: Modal ──────────────────────────────────────── */}
      {showReminderModal && (
        <div
          onClick={(e) => {
            if (e.target === e.currentTarget && !reminderLoading) {
              setShowReminderModal(false);
            }
          }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px'
          }}
        >
          <div style={{
            background: '#ffffff',
            borderRadius: '4px',
            width: '100%',
            maxWidth: '520px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
            overflow: 'hidden'
          }}>
            {/* Modal Header */}
            <div style={{ padding: '24px 24px 0 24px', fontFamily: "'Segoe UI', sans-serif" }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '8px',
                backgroundColor: '#FEF0EC',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '16px'
              }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#F3694A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
              </div>
              <h3 style={{
                margin: '0 0 8px 0',
                fontSize: '15px',
                fontWeight: '600',
                color: '#242424',
                fontFamily: "'Segoe UI', sans-serif"
              }}>
                Send Reminder
              </h3>
              <p style={{
                margin: '0',
                fontSize: '14px',
                color: '#6b7280',
                lineHeight: '1.5',
                fontFamily: "'Segoe UI', sans-serif"
              }}>
                A reminder email will be sent to all people with pending tasks for this request. You can send one reminder per day.
              </p>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '20px 24px' }}>
              <div style={{ marginBottom: '4px' }}>
                <label style={{
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#374151',
                  display: 'block',
                  marginBottom: '8px'
                }}>
                  Custom Message <span style={{ color: '#9ca3af', fontWeight: '400' }}>(optional)</span>
                </label>
                <textarea
                  value={reminderMessage}
                  onChange={(e) => setReminderMessage(e.target.value)}
                  maxLength={1000}
                  placeholder="Add a personal note to include in the reminder email..."
                  style={{
                    width: '100%',
                    minHeight: '100px',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '14px',
                    color: '#111827',
                    lineHeight: '1.5',
                    resize: 'vertical',
                    fontFamily: 'inherit',
                    outline: 'none',
                    boxSizing: 'border-box',
                    transition: 'border-color 0.2s ease'
                  }}
                  onFocus={(e) => { e.currentTarget.style.borderColor = '#242424'; }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = '#d1d5db'; }}
                />
                <div style={{
                  fontSize: '11px',
                  color: reminderMessage.length > 900 ? '#ef4444' : '#9ca3af',
                  textAlign: 'right',
                  marginTop: '4px'
                }}>
                  {reminderMessage.length}/1000
                </div>
              </div>

              {/* Info box */}
              <div style={{
                backgroundColor: '#f0f9ff',
                border: '1px solid #bae6fd',
                borderRadius: '8px',
                padding: '12px 16px',
                fontSize: '13px',
                color: '#0369a1',
                lineHeight: '1.5'
              }}>
                <strong>ℹ️ Note:</strong> The reminder will be sent to all assignees who have pending tasks for Request #{requestData.id}. The email will include request details and your custom message.
              </div>

              {/* Error message inside modal */}
              {reminderResult && reminderResult.type === 'error' && (
                <div style={{
                  marginTop: '12px',
                  padding: '12px 16px',
                  backgroundColor: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '6px',
                  fontSize: '13px',
                  color: '#991b1b',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span>❌</span>
                  {reminderResult.message}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '16px 24px 24px 24px',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              fontFamily: "'Segoe UI', sans-serif"
            }}>
              <button
                onClick={() => setShowReminderModal(false)}
                disabled={reminderLoading}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#ffffff',
                  color: '#242424',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  cursor: reminderLoading ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  fontFamily: "'Segoe UI', sans-serif"
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSendReminder}
                disabled={reminderLoading}
                style={{
                  padding: '8px 20px',
                  backgroundColor: reminderLoading ? '#f9a68e' : '#F3694A',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: reminderLoading ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  fontFamily: "'Segoe UI', sans-serif",
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.15s ease'
                }}
              >
                {reminderLoading ? (
                  <>
                    <span style={{
                      display: 'inline-block',
                      width: '14px',
                      height: '14px',
                      border: '2px solid rgba(255,255,255,0.3)',
                      borderTopColor: '#fff',
                      borderRadius: '50%',
                      animation: 'spin 0.8s linear infinite'
                    }} />
                    Sending...
                  </>
                ) : (
                  <>Send Reminder</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ─── END: Reminder Modal ──────────────────────────────────── */}

      {/* ─── Parent Request Detail Popup ──────────────────────────── */}
      {showParentPopup && requestData.parentRequestId && getAccessToken && (
        <ParentRequestDetailPopup
          isOpen={showParentPopup}
          parentRequestId={requestData.parentRequestId}
          getAccessToken={getAccessToken}
          onClose={() => setShowParentPopup(false)}
          regions={regions}
        />
      )}
    </div>
    
  );
};